var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useContext } from 'react';
import { DialogContent } from '@reach/dialog';
import FocusLock from 'react-focus-lock';
import { hideOthers } from 'aria-hidden';
import { __RouterContext } from 'react-router';
import styled, { css } from 'styled-components';
import { useRouteChange } from '@ally/use-route-change';
import { Box } from '../Box';
import { CloseButton, CloseButtonSpacer } from '../tools/components';
import { ModalContext, useModalContext, useModal, } from './ModalContext';
import { useModalObserver } from './useModalObserver';
import { getContentWidth, getContentMaxWidth } from './utils';
import { a11y } from './strings';
import { upToSm } from '../tools/utils';
var fullScreenStyles = css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  width: 100vw;\n  height: 100%;\n  margin: 0;\n  overflow-y: scroll;\n  border-radius: 0;\n  box-shadow: none;\n  display: flex;\n  flex-direction: column;\n"], ["\n  width: 100vw;\n  height: 100%;\n  margin: 0;\n  overflow-y: scroll;\n  border-radius: 0;\n  box-shadow: none;\n  display: flex;\n  flex-direction: column;\n"])));
var StyledDialogContent = styled(DialogContent)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var _b = _a.theme, media = _b.media, colors = _b.colors, space = _b.space, $fullScreenSmDown = _a.$fullScreenSmDown, $fullScreenMdOnly = _a.$fullScreenMdOnly, $height = _a.$height, _c = _a.$width, $width = _c === void 0 ? 'lg' : _c;
    return "\n      position: relative;\n      background: ".concat(colors.white, ";\n      height: ").concat($height || 'fit-content', ";\n      box-sizing: border-box;\n      max-width: 100vw;\n      margin: ").concat(space.lg, " 0;\n      outline: none;\n      overflow: hidden;\n      border-radius: 4px;\n      box-shadow: 0 2px 4px -2px rgba(0,0,0,0.16), 0 5px 8px -4px rgba(0,0,0,0.16); \n\n      ").concat(media.down('sm'), " {\n        width: ").concat(getContentWidth({
        modalWidth: $width,
        screenWidth: 'sm-down',
    }), ";\n        ").concat($fullScreenSmDown ? "".concat(fullScreenStyles) : '', "\n      }\n\n      ").concat(media.between('md', 'md'), " {\n        width: ").concat(getContentWidth({
        modalWidth: $width,
        screenWidth: 'md-only',
    }), ";\n        ").concat($fullScreenMdOnly ? "".concat(fullScreenStyles) : '', "\n      }\n\n      ").concat(media.up('lg'), " {\n        width: ").concat(getContentWidth({ modalWidth: $width, screenWidth: 'lg-up' }), ";\n        max-width: ").concat(getContentMaxWidth($width), ";\n      }\n  ");
});
var StyledBox = styled(Box)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var media = _a.theme.media, $fullScreenSmDown = _a.$fullScreenSmDown, $fullScreenMdOnly = _a.$fullScreenMdOnly;
    return "\n    ".concat(media.down('sm'), " {\n      ").concat($fullScreenSmDown ? "flex: 1;" : '', "\n    }\n\n    ").concat(media.between('md', 'md'), " {\n      ").concat($fullScreenMdOnly ? "flex: 1;" : '', "\n    }\n  ");
});
var useLocation = function () {
    var values = useContext(__RouterContext);
    return values === null || values === void 0 ? void 0 : values.location;
};
var StyledFocusLock = styled(FocusLock)(function (_a) {
    var media = _a.theme.media, $fullScreenSmDown = _a.$fullScreenSmDown, $fullScreenMdOnly = _a.$fullScreenMdOnly;
    return css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    ", " {\n      ", "\n    }\n    ", " {\n      ", "\n    }\n  "], ["\n    ", " {\n      ", "\n    }\n    ", " {\n      ", "\n    }\n  "])), media.down('sm'), $fullScreenSmDown ? 'height: 100%' : '', media.between('md', 'md'), $fullScreenMdOnly ? 'height: 100%' : '');
});
var StyledOverlay = styled.div(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
    var $fullScreenSmDown = _a.$fullScreenSmDown, $fullScreenMdOnly = _a.$fullScreenMdOnly, $isIntersectingViewport = _a.$isIntersectingViewport, media = _a.theme.media;
    return "\n    background: hsla(0, 0%, 0%, 0.3);\n    position: fixed;\n    top: 0;\n    right: 0;\n    left: 0;\n    height: 100%;\n    overflow: auto;\n    z-index: 10;\n    display: flex;\n    justify-content: center;\n    align-items: ".concat($isIntersectingViewport ? 'flex-start' : 'center', ";\n\n    ").concat(media.down('sm'), " {\n      ").concat($fullScreenSmDown ? 'align-items: flex-start;' : '', " \n    }\n\n    ").concat(media.between('md', 'md'), " {\n      ").concat($fullScreenMdOnly ? 'align-items: flex-start;' : '', " \n    }\n  ");
});
var Overlay = function (_a) {
    var onDismiss = _a.onDismiss, $fullScreenSmDown = _a.$fullScreenSmDown, $fullScreenMdOnly = _a.$fullScreenMdOnly, $isIntersectingViewport = _a.$isIntersectingViewport, children = _a.children;
    var _b = useModal(), show = _b.show, closeModal = _b.closeModal;
    var handleOnDismiss = function () {
        closeModal();
        onDismiss();
    };
    if (show) {
        return (React.createElement(StyledOverlay, { onClick: handleOnDismiss, "$fullScreenSmDown": $fullScreenSmDown, "$fullScreenMdOnly": $fullScreenMdOnly, "$isIntersectingViewport": $isIntersectingViewport, "data-testid": "modal-overlay" }, children));
    }
    return null;
};
var DialogContentWrapper = function (_a) {
    var content = _a.content, disableDismiss = _a.disableDismiss, focusRef = _a.focusRef, disableFullScreenSmDown = _a.disableFullScreenSmDown, fullScreenMd = _a.fullScreenMd, height = _a.height, width = _a.width, hideClose = _a.hideClose, role = _a.role, onDismissCallback = _a.onDismissCallback, dataTrackRegion = _a.dataTrackRegion, _b = _a.toggleCallback, toggleCallback = _b === void 0 ? function (isOpen) {
        /* noop */
    } : _b, disableReturnFocus = _a.disableReturnFocus, className = _a.className, ariaLabelCloseButton = _a.ariaLabelCloseButton;
    var _c = useModal(), show = _c.show, showModal = _c.showModal, closeModal = _c.closeModal, originallyFocusedElement = _c.originallyFocusedElement;
    var ContentElement = React.cloneElement(content, {
        showModal: showModal,
        closeModal: closeModal,
        show: show,
    });
    var _d = useModalObserver({
        isModalShowing: show,
    }), dialogContentRef = _d.dialogContentRef, isIntersectingViewport = _d.isIntersectingViewport;
    var _e = __read(React.useState(null), 2), dialogRef = _e[0], setDialogRef = _e[1];
    React.useEffect(function () {
        var undo;
        if (show && dialogRef) {
            undo = hideOthers(dialogRef);
        }
        return function () {
            if (undo) {
                undo();
            }
        };
    }, [show, dialogRef]);
    var onActivation = React.useCallback(function (el) {
        var _a, _b, _c;
        setDialogRef(el);
        if (typeof focusRef === 'object') {
            ;
            (_c = (_b = (_a = focusRef) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.focus) === null || _c === void 0 ? void 0 : _c.call(_b);
        }
        if (typeof focusRef === 'function') {
            focusRef(el);
        }
    }, [focusRef]);
    var onDeactivation = React.useCallback(function () {
        setDialogRef(null);
        if (!disableReturnFocus &&
            originallyFocusedElement instanceof HTMLElement) {
            originallyFocusedElement.focus();
        }
    }, [originallyFocusedElement]);
    var onDismiss = React.useCallback(function () {
        if (!disableDismiss) {
            closeModal();
            toggleCallback(false);
        }
    }, [disableDismiss, closeModal, toggleCallback]);
    var onClickCloseButton = React.useCallback(function () {
        closeModal();
        toggleCallback(false);
        if (onDismissCallback) {
            onDismissCallback();
        }
    }, [closeModal, toggleCallback, onDismissCallback]);
    return (React.createElement(Overlay, { "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, "$isIntersectingViewport": isIntersectingViewport, onDismiss: onDismiss },
        React.createElement(StyledFocusLock, { "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, onActivation: onActivation, onDeactivation: onDeactivation, hasPositiveIndices: false },
            React.createElement(StyledDialogContent, { ref: dialogContentRef, "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, "$height": height, "$width": width, role: role, "data-track-region": dataTrackRegion, className: className, "data-testid": "mui-modal-container" },
                !hideClose && (React.createElement(CloseButtonSpacer, { isFullScreenSmDown: !disableFullScreenSmDown })),
                React.createElement(StyledBox, { p: upToSm('md', 'lg'), "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd }, ContentElement),
                !hideClose && (React.createElement(CloseButton, { "data-testid": "close-modal", "data-track-elem": "icon", "data-track-name": "Close", "data-track-trigger": "close", ariaLabel: ariaLabelCloseButton, onClick: onClickCloseButton }))))));
};
var Modal = React.forwardRef(function (props, ref) {
    var content = props.content, _a = props.disableDismiss, disableDismiss = _a === void 0 ? false : _a, disableFullScreenSmDown = props.disableFullScreenSmDown, fullScreenMd = props.fullScreenMd, height = props.height, width = props.width, hideClose = props.hideClose, manualToggle = props.manualToggle, role = props.role, setShowOption = props.setShowOption, showOnLoad = props.showOnLoad, _b = props.toggleCallback, toggleCallback = _b === void 0 ? function (isOpen) {
        /* noop */
    } : _b, _c = props.trigger, trigger = _c === void 0 ? null : _c, disableCloseOnRouteChange = props.disableCloseOnRouteChange, onDismissCallback = props.onDismissCallback, disableReturnFocus = props.disableReturnFocus, className = props.className, context = props.context, _d = props.ariaLabelCloseButton, ariaLabelCloseButton = _d === void 0 ? a11y.ariaLabelCloseButton : _d;
    var defaultContext = useModalContext({ initialShow: showOnLoad });
    var safeContext = context || defaultContext;
    var setShow = safeContext.setShow;
    var currentRoute = useLocation();
    useRouteChange((currentRoute === null || currentRoute === void 0 ? void 0 : currentRoute.pathname) || '', function () {
        if (currentRoute && !disableCloseOnRouteChange) {
            setShow(false);
        }
    });
    React.useEffect(function () {
        if (manualToggle)
            setShow(!!setShowOption);
    }, [manualToggle, setShowOption, setShow]);
    return (React.createElement(ModalContext.Provider, { value: safeContext },
        trigger &&
            React.cloneElement(trigger, {
                openModal: function () {
                    setShow(true);
                    toggleCallback(true);
                },
            }),
        React.createElement(DialogContentWrapper, { content: content, disableDismiss: disableDismiss, focusRef: ref, disableFullScreenSmDown: disableFullScreenSmDown, fullScreenMd: fullScreenMd, height: height, width: width, hideClose: hideClose, role: role, toggleCallback: toggleCallback, onDismissCallback: onDismissCallback, disableReturnFocus: disableReturnFocus, className: className, ariaLabelCloseButton: ariaLabelCloseButton })));
});
var ModalForProps = function () { return React.createElement(React.Fragment, null); };
export { Modal, ModalForProps };
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;
//# sourceMappingURL=Modal.js.map